/**
 * Sampled source: two AudioBufferSourceNode slots over the nearest multisample root, residual pitch via
 * detune.setTargetAtTime, root switches as a 100 ms crossfade with the new slot started at the same loop phase.
 */
import { hzToMidi } from '@tonist/shared';
import { chooseRoot, type RootBuffer } from './sampler';
import { FADE_IN, FADE_OUT, glideTo, holdParam } from './params';
import { makeRng } from './modulation';
import { effectiveHz, safeStop, type SourceUnit, type UnitConfig } from './units';

interface Slot {
  src: AudioBufferSourceNode;
  gain: GainNode;
  root: RootBuffer;
  phase0: number;
  t0: number;
  rate: number;
}

const XFADE_SEC = 0.1;

export class SampledUnit implements SourceUnit {
  readonly output: GainNode;
  private slots: Slot[] = [];
  private readonly modulators: AudioNode[] = [];
  private currentRoot: number | undefined;
  private stopped = false;
  private readonly rng: () => number;

  constructor(private readonly cfg: UnitConfig, private readonly roots: readonly RootBuffer[], private readonly single: boolean) {
    if (roots.length === 0) throw new Error('SampledUnit needs at least one root');
    this.output = cfg.ctx.createGain();
    this.output.gain.value = cfg.level;
    this.rng = makeRng(cfg.seed);
  }

  private pick(midi: number): RootBuffer {
    // `roots` is shared with the drone runtime and upgraded in place when background rendering finishes
    const m = this.single ? this.roots[0]!.midi : chooseRoot(this.roots.map((r) => r.midi), midi, this.currentRoot);
    return this.roots.find((r) => r.midi === m) ?? this.roots[0]!;
  }

  private phaseAt(s: Slot, t: number): number {
    const len = s.root.loopEnd - s.root.loopStart;
    if (len <= 0) return 0;
    const p = s.phase0 + ((t - s.t0) * s.rate) / len;
    return p - Math.floor(p);
  }

  /**
   * Where to start the incoming root so its waveform lines up with the one still playing.
   *
   * Two renders of the same loop hold the same partials at unrelated phases, so crossfading between them at the same
   * pitch punches a hole where they cancel. Both are quasi-periodic, though, so sliding the new one by up to a period
   * and keeping the best correlation puts them in phase — after which the fade is inaudible. Returns an offset in
   * seconds inside the new buffer's loop.
   */
  private alignedOffset(old: Slot, root: RootBuffer, t: number, newCents: number): number {
    const nominal = root.loopStart + this.phaseAt(old, t) * (root.loopEnd - root.loopStart);
    const oldBuf = old.root.buffer, newBuf = root.buffer;
    const sr = oldBuf.sampleRate;
    if (newBuf.sampleRate !== sr) return nominal;
    const rateOld = Math.pow(2, old.src.detune.value / 1200);
    const rateNew = Math.pow(2, newCents / 1200);
    const hzOut = Math.pow(2, (old.root.midi + old.src.detune.value / 100 - 69) / 12) * 440;
    if (!(hzOut > 0) || !Number.isFinite(rateOld) || !Number.isFinite(rateNew)) return nominal;
    const periodOut = sr / hzOut;                       // one period, in output samples
    const window = Math.min(4096, Math.round(2 * periodOut));
    const stride = Math.max(1, Math.round(window / 256));
    const search = Math.round(1.2 * periodOut * rateNew);
    const oldData = oldBuf.getChannelData(0), newData = newBuf.getChannelData(0);
    const oldStart = (old.root.loopStart + this.phaseAt(old, t) * (old.root.loopEnd - old.root.loopStart)) * sr;
    if (oldStart + window * rateOld + 2 >= oldData.length || search <= 0) return nominal;
    const ref: number[] = [];
    let refEnergy = 0;
    for (let i = 0; i < window; i += stride) {
      const p = oldStart + i * rateOld; const p0 = Math.floor(p);
      const v = oldData[p0]! + (oldData[p0 + 1]! - oldData[p0]!) * (p - p0);
      ref.push(v); refEnergy += v * v;
    }
    if (refEnergy <= 1e-12) return nominal;
    let best = nominal, bestScore = -Infinity;
    const loopLen = (root.loopEnd - root.loopStart) * sr;
    for (let d = -search; d <= search; d++) {
      let pos = nominal * sr + d;
      // keep the whole window inside the buffer
      if (pos < 0 || pos + window * rateNew + 2 >= newData.length) continue;
      let dot = 0, energy = 0;
      for (let k = 0; k < ref.length; k++) {
        const p = pos + k * stride * rateNew; const p0 = Math.floor(p);
        const v = newData[p0]! + (newData[p0 + 1]! - newData[p0]!) * (p - p0);
        dot += ref[k]! * v; energy += v * v;
      }
      const score = energy > 1e-12 ? dot / Math.sqrt(energy) : 0;
      if (score > bestScore) { bestScore = score; best = pos / sr; }
    }
    // keep it inside the loop so the source wraps where it should
    if (loopLen > 0) {
      while (best < root.loopStart) best += loopLen / sr;
      while (best >= root.loopEnd) best -= loopLen / sr;
    }
    return best;
  }

  private makeSlot(root: RootBuffer, phase: number, cents: number, t: number, offsetSec?: number): Slot {
    const ctx = this.cfg.ctx;
    const src = ctx.createBufferSource();
    src.buffer = root.buffer;
    src.loop = true;
    src.loopStart = root.loopStart;
    src.loopEnd = root.loopEnd;
    src.detune.value = cents;
    for (const m of this.modulators) m.connect(src.detune);
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(gain);
    gain.connect(this.output);
    const len = Math.max(0, root.loopEnd - root.loopStart);
    const offset = offsetSec ?? root.loopStart + phase * len;
    src.start(t, offset);
    return { src, gain, root, phase0: len > 0 ? (offset - root.loopStart) / len : 0, t0: t, rate: Math.pow(2, cents / 1200) };
  }

  start(t: number, hz: number): void {
    const midi = hzToMidi(effectiveHz(hz, this.cfg.cents));
    const root = this.pick(midi);
    this.currentRoot = root.midi;
    const cents = (midi - root.midi) * 100;
    const s = this.makeSlot(root, this.rng() * this.cfg.phaseScatter, cents, t);
    s.gain.gain.setValueAtTime(1, t);
    this.slots.push(s);
  }

  setPitch(hz: number, glideSec: number, t: number): void {
    if (this.stopped) return;
    const active = this.slots[this.slots.length - 1];
    if (!active) return;
    const midi = hzToMidi(effectiveHz(hz, this.cfg.cents));
    const root = this.pick(midi);
    const cents = (midi - root.midi) * 100;
    if (root.midi === active.root.midi) {
      active.phase0 = this.phaseAt(active, t);
      active.t0 = t;
      active.rate = Math.pow(2, cents / 1200);
      glideTo(active.src.detune, cents, t, glideSec);
      return;
    }
    // Root switch: the new slot starts at the same loop phase and at the pitch the old slot is at right now;
    // both glide to the target while an equal-gain (correlated, same-phase) crossfade swaps them.
    this.currentRoot = root.midi;
    const currentCents = active.src.detune.value;
    const startCents = currentCents + (active.root.midi - root.midi) * 100;
    const next = this.makeSlot(root, this.phaseAt(active, t), startCents, t, this.alignedOffset(active, root, t, startCents));
    glideTo(next.src.detune, cents, t, glideSec);
    glideTo(active.src.detune, cents + (root.midi - active.root.midi) * 100, t, glideSec);
    next.gain.gain.cancelScheduledValues(t);
    next.gain.gain.setValueCurveAtTime(FADE_IN, t, XFADE_SEC);
    const olds = this.slots;
    for (const old of olds) {
      old.gain.gain.cancelScheduledValues(t);
      old.gain.gain.setValueCurveAtTime(FADE_OUT, t, XFADE_SEC);
      safeStop(old.src, t + XFADE_SEC + 0.05);
    }
    this.slots = [next];
    setTimeout(() => { for (const o of olds) { try { o.src.disconnect(); o.gain.disconnect(); } catch { /* gone */ } } }, (XFADE_SEC + 0.4) * 1000);
  }

  attachDetuneModulator(node: AudioNode): void {
    this.modulators.push(node);
    for (const s of this.slots) node.connect(s.src.detune);
  }

  stop(t: number): void {
    this.stopped = true;
    for (const s of this.slots) safeStop(s.src, t);
  }

  dispose(): void {
    const now = this.cfg.ctx.currentTime;
    for (const s of this.slots) { safeStop(s.src, now); try { s.src.disconnect(); s.gain.disconnect(); } catch { /* gone */ } }
    this.slots = [];
    try { this.output.disconnect(); } catch { /* gone */ }
  }
}
